/**
 * Shared park-fence geometry constants + helpers for the outfield-wall diagrams.
 *
 * Two surfaces draw fences against league average: the slate `ParkDiagram` (a
 * stylized skyline) and the research `SprayChart` (a polar field plot). They keep
 * their own SVG geometry and color palettes (the spray chart runs higher-contrast
 * so the EV dots read over it), but the league-average reference and the
 * short-vs-deep classification MUST agree — otherwise the same park reads as
 * hitter-friendly on one surface and neutral on the other. Both live here so a
 * 2026 league-average recompute or a threshold retune lands in exactly one place.
 */

/** League-average outfield fences (ft), from MLB API fieldInfo across all 30 venues (2026). */
export const LEAGUE_AVG_FENCES = {
  leftLine: 333,
  leftCenter: 385,
  center: 404,
  rightCenter: 381,
  rightLine: 329,
} as const;

export type FenceFriendliness = 'short' | 'deep' | 'neutral';

/**
 * Classify a wall segment vs league average by the mean deviation of its two
 * endpoints: short (≥4 ft under) is hitter-friendly, deep (≥4 ft over) is
 * pitcher-friendly. Callers map the band to their own palette.
 */
export function fenceFriendliness(v1: number, avg1: number, v2: number, avg2: number): FenceFriendliness {
  const dev = ((v1 - avg1) + (v2 - avg2)) / 2;
  if (dev <= -4) return 'short';
  if (dev >= 4) return 'deep';
  return 'neutral';
}
